import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  AccessTime,
  Language,
  Business
} from '@mui/icons-material';
import LegalLayout from './LegalLayout';
import api from '../../services/api';

interface ContactForm {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await api.post('/contact', {
        ...formData,
        inquiry_type: 'general',
      });
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err: any) {
      const message = err.response?.data?.message
        || err.response?.data?.error
        || 'Възникна грешка. Моля, опитайте отново или ни пишете на info@goldenbooks.bg';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LegalLayout title="Контакти">
      <Typography paragraph>
        Свържете се с нас по всяко време! Нашият екип е на Ваше разположение за 
        въпроси, предложения или помощ при избора на книги.
      </Typography>

      <Grid container spacing={4} sx={{ mt: 2 }}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                Информация за контакт
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Business color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Компания"
                    secondary="Golden Books Ltd"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Email color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Имейл"
                    secondary="info@goldenbooks.bg"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Phone color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Телефон"
                    secondary="0895628293"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <LocationOn color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Адрес"
                    secondary="София, България"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <AccessTime color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Работно време"
                    secondary="Понеделник - Петък: 9:00 - 18:00 ч."
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Language color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Уебсайт"
                    secondary="www.goldenbooks.bg"
                  />
                </ListItem>
              </List>
              
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Отдели за контакт:
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Общи въпроси:</strong> info@goldenbooks.bg<br />
                <strong>Поръчки:</strong> orders@goldenbooks.bg<br />
                <strong>Доставки:</strong> delivery@goldenbooks.bg<br />
                <strong>Връщания:</strong> returns@goldenbooks.bg<br />
                <strong>Технически проблеми:</strong> support@goldenbooks.bg
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                Изпратете ни съобщение
              </Typography>
              
              {submitted ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Благодарим Ви! Вашето съобщение беше изпратено успешно.
                  Ще се свържем с Вас в най-скоро време.
                </Alert>
              ) : (
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    name="name"
                    label="Име и фамилия"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    name="email"
                    label="Имейл адрес"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    name="subject"
                    label="Тема"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    inputProps={{ minLength: 5 }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    name="message"
                    label="Съобщение"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    inputProps={{ minLength: 20 }}
                    helperText="Минимум 20 символа"
                    sx={{ mb: 2 }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {loading ? 'Изпращане...' : 'Изпрати съобщение'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Information */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Честo задавани въпроси
        </Typography>
        
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              За поръчки:
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Как мога да направя поръчка?</strong><br />
              Можете да поръчате онлайн чрез нашия сайт или по телефона.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Мога ли да променя поръчката си?</strong><br />
              Да, ако поръчката още не е изпратена. Свържете се с нас веднага.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              За доставки:
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Колко време отнема доставката?</strong><br />
              Стандартната доставка е 3 работни дни за цялата страна.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Безплатна ли е доставката?</strong><br />
              Да, за поръчки над 150 € доставката е безплатна.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Business Hours */}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Работно време на екипа за обслужване
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Понеделник - Петък:</strong> 9:00 - 18:00 ч.<br />
              <strong>Събота:</strong> 10:00 - 16:00 ч.<br />
              <strong>Неделя:</strong> Почивен ден
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Празници:</strong> Съгласно официалния календар<br />
              <strong>Извънредни ситуации:</strong> Ще бъдете уведомени<br />
              <strong>Онлайн поръчки:</strong> 24/7
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </LegalLayout>
  );
};

export default Contact;